import React, { useState } from "react";

import { IgCodeEditor, IgDivider, IgTypography } from "@intelligems/ig-theme";
import type { GenericComponent } from "@src/store/slices/experienceBuilder/types";
import { Tab, Tabs } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { ConfigField } from "@src/features/experienceBuilder/modifications/components/generic/ConfigField";
import {
  parseDefinition,
  rebuildDefinition,
} from "@src/features/experienceBuilder/modifications/components/generic/parseDefinition";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { ElementMeta } from "@src/features/experienceBuilder/modifications/common/panel/ElementMeta";

export const GenericComponentPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mod = useSelector(Selector.component.getVariationDraft) as
    | GenericComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);

  const parsed = parseDefinition(mod.definition);
  const hasParsed = parsed !== null;
  const [tab, setTab] = useState(hasParsed ? 0 : 2);

  const update = (changes: Partial<GenericComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  const errors = useSelector(Selector.getVariationDraftUIErrors);

  const handleDefinitionChange = (definition: string) => {
    update({ definition });
  };

  const handleTemplateChange = (innerHTML: string) => {
    if (!parsed) return;
    update({ definition: rebuildDefinition(parsed.attrs, innerHTML) });
  };

  const handleAttrChange = (attrName: string, value: string) => {
    if (!parsed) return;
    const newAttrs = parsed.attrs.map((a) =>
      a.name === attrName ? { ...a, value } : a
    );
    update({
      definition: rebuildDefinition(newAttrs, parsed.innerHTML),
    });
  };

  return (
    <>
      <ModificationHeader />

      <Panel>
        <Panel.Body>
          <ElementMeta mod={mod} errors={errors} onChange={update} />

          <IgDivider />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 4,
              borderBottom: `1px solid ${theme.igBorders.core.default}`,
            }}
          >
            <Tab label="Config" disabled={!hasParsed} />
            <Tab label="Template" disabled={!hasParsed} />
          </Tabs>
          {/* Config tab */}
          {tab === 0 && parsed && (
            <Stack spacing={3}>
              {parsed.attrs
                .filter((attr) => attr.prefix !== null)
                .map((attr) => (
                  <ConfigField
                    key={attr.name}
                    attr={attr}
                    onChange={(v) => handleAttrChange(attr.name, v)}
                  />
                ))}
              {parsed.attrs.every((attr) => attr.prefix === null) && (
                <IgTypography variant="body2" color="text.secondary">
                  No prefixed attributes found. Add attributes like color:bg,
                  text:title, number:count, etc.
                </IgTypography>
              )}
            </Stack>
          )}

          {/* Template tab */}
          {tab === 1 && parsed && (
            <IgCodeEditor
              autoFocus
              language="html"
              height="400px"
              value={parsed.innerHTML}
              onChange={(v) => handleTemplateChange(v ?? "")}
            />
          )}
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
